/** CLE-446: strict, review-only transport for API-validated search criteria.
 * Uses the existing q codec and filter defaults, never the lossy legacy migrator.
 * Contract: api/docs/plans/cle-446-mcp-search-table-handoff.md, Phase 2.
 */
import { INITIAL_FILTERS, type SearchFilters } from '@/types/search-filters';
import {
  INITIAL_COMPANY_FILTERS,
  buildCompanyFilters,
  HEADCOUNT_RANGES,
  FUNDING_ROUND_TYPES,
  type CompanySearchFilters,
} from '@/utils/company-search.utils';
import { validateCompanyExclusions } from '@/utils/company-exclusions.util';
import { detectCompositeRoute } from '@/utils/composite-search-router';
import { decodeSearchQuery } from '@/utils/search-query-encoding';
import { COMPANY_SIZE_VALUES } from '@/constants/company-size-buckets';
import { SENIORITY_LEVELS } from '@/constants/seniority-levels';
import { INDUSTRIES } from '@/constants/industries';
import { INDUSTRY_GROUP_NAMES } from '@/constants/industry-groups';
import { LINKEDIN_FUNCTIONS } from '@/constants/linkedin-functions';
import { OPEN_TO_CARD_OPTIONS } from '@/constants/open-to-card-types';
import { STOCK_LISTING_OPTIONS } from '@/constants/crustdata-stock-listings';
import { WORKFORCE_ROLES } from '@/components/search/shared/role-percent-rule-builder.component';
import {
  expandCountrySelectionToDisplayNames,
  expandCountrySelectionToFullNames,
} from '@/constants/country-groups';
import { addContainsFilter, addFuzzyFilter } from '@/utils/people-search.utils';
import { INSTANTLY_STATUS_LABELS } from './people-exclusion-validation';
import { ATTIO_SEARCH_PAGE_REFUSAL } from '@/utils/agent-filters-to-search-filters';

export const MCP_HANDOFF_URL_LIMIT = 8192;
const MCP_COMPOSITE_KEY = '__compositeCompanyFilters';
const SORT_COLUMN = /^[A-Za-z_][A-Za-z0-9_.]{0,127}$/;
// API vocab_company.COMPANY_TYPES. These are actual provider enum spellings;
// imported active-filter chips retain them verbatim even where the old picker
// still offers a different spelling. Ordinary picker behavior is unchanged.
export const MCP_COMPANY_TYPES = [
  'Privately Held',
  'Public Company',
  'Nonprofit',
  'Government Agency',
  'Partnership',
  'Self-Employed',
  'Sole Proprietorship',
  'Educational Institution',
] as const;
const PRIVATE_PEOPLE_FIELDS = [
  'firstName',
  'lastName',
  'linkedinUrls',
  'twitterHandle',
  'excludeProfileUrls',
  'excludeCrmOwner',
  'excludeInCleanlistListIds',
] as const;
// The part of the list that looks one person up. An audience search never
// carries these, so no transport accepts them.
const PERSON_LOOKUP_FIELDS: readonly string[] = [
  'firstName',
  'lastName',
  'linkedinUrls',
  'twitterHandle',
];

export interface SearchFilterOptions {
  /** CLE-550: chat Clu builds its own q from a server-validated tool result,
   * so the saved exclusions the MCP URL refuses may ride along: specific
   * profile URLs, Cleanlist list ids, a CRM owner and `owner:<id>` clauses.
   * Looking one person up (name, LinkedIn URLs, Twitter handle) stays refused.
   * Off for the MCP link. */
  allowExclusions?: boolean;
  /** CLE-550: the company criteria may carry the Find Companies excludes
   * (`excludeCompanyDomains`, `excludeInCleanlist*`, CRM clauses). They are
   * checked for shape either way; without this they are refused, because a
   * page that cannot send them must not open as if they were applied. The
   * chat handoff sets it. */
  companyExcludes?: boolean;
}

export interface McpSearchContext {
  limit: number;
  crm: 'salesforce' | 'hubspot' | null;
  sort: string | null;
  organizationId: string;
  /** Derived from sparse input, not another serialized control map. */
  maxResultsExplicit: boolean;
  hasExplicitCompanyCriteria: boolean;
}

export interface McpSearchRestoration {
  tab: 'people' | 'company';
  people: SearchFilters;
  company: CompanySearchFilters;
  composite: CompanySearchFilters;
  context: McpSearchContext;
}

export type McpHandoffDecode =
  | { kind: 'ordinary' }
  | { kind: 'invalid'; message: string }
  | { kind: 'valid'; value: McpSearchRestoration };

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
const hasOwn = (value: object, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);

function fail(message: string): never {
  throw new Error(`Cannot restore MCP search: ${message}`);
}

function exactKeys(
  value: Record<string, unknown>,
  keys: string[],
  label: string
) {
  if (
    Object.keys(value).some((key) => !keys.includes(key)) ||
    keys.some((key) => !hasOwn(value, key))
  ) {
    fail(`${label} has missing or unsupported fields.`);
  }
}

function choice(value: unknown, values: readonly unknown[], label: string) {
  if (!values.includes(value)) fail(`${label} has an unsupported value.`);
}

function nestedFilter(
  key: string,
  value: unknown,
  options: SearchFilterOptions
) {
  if (key === 'geoRadius') {
    if (value === null) return;
    if (!record(value)) fail('geoRadius must be an object or null.');
    if (
      Object.keys(value).some(
        (field) => !['center', 'radius', 'unit'].includes(field)
      )
    )
      fail('geoRadius has unsupported fields.');
    if (
      typeof value.center !== 'string' ||
      !value.center.trim() ||
      typeof value.radius !== 'number' ||
      !Number.isFinite(value.radius) ||
      value.radius <= 0
    ) {
      fail('geoRadius requires a center and positive finite radius.');
    }
    if (value.unit !== undefined)
      choice(value.unit, ['km', 'mi'], 'geoRadius.unit');
  } else if (key === 'crmFilterSections') {
    if (!record(value)) fail('crmFilterSections must be an object.');
    // The page has no Attio section; the clause is refused, never dropped.
    if (hasOwn(value, 'attio')) fail(ATTIO_SEARCH_PAGE_REFUSAL);
    if (
      Object.keys(value).some(
        (crm) => crm !== 'salesforce' && crm !== 'hubspot'
      )
    ) {
      fail('crmFilterSections has an unsupported CRM.');
    }
    for (const [crm, clauses] of Object.entries(value)) {
      if (!Array.isArray(clauses)) fail(`${crm} clauses must be an array.`);
      for (const clause of clauses) {
        if (!record(clause)) fail(`${crm} clause must be an object.`);
        // Pydantic exclude_unset also omits the default polarity.
        if (
          Object.keys(clause).some(
            (field) => !['id', 'polarity', 'condition'].includes(field)
          ) ||
          typeof clause.id !== 'string' ||
          !clause.id.trim() ||
          typeof clause.condition !== 'string' ||
          !clause.condition.trim()
        ) {
          fail(`${crm} clause requires a nonblank id and condition.`);
        }
        if (clause.polarity !== undefined)
          choice(clause.polarity, ['include', 'exclude'], 'CRM polarity');
        if (
          !options.allowExclusions &&
          /^owner:/i.test(clause.condition.trim())
        )
          fail('private CRM owner identifiers cannot be carried in a URL.');
      }
    }
  } else if (key === 'workforceRules') {
    if (!Array.isArray(value)) fail('workforceRules must be an array.');
    for (const rule of value) {
      if (!record(rule)) fail('workforceRules contains a malformed rule.');
      exactKeys(rule, ['role', 'metric', 'op', 'threshold'], 'workforce rule');
      if (
        typeof rule.role !== 'string' ||
        !rule.role.trim() ||
        typeof rule.threshold !== 'number' ||
        !Number.isFinite(rule.threshold)
      )
        fail('workforce rule requires a role and finite threshold.');
      choice(
        rule.metric,
        ['percent', 'growth_6m', 'growth_yoy'],
        'workforce metric'
      );
      choice(rule.role, WORKFORCE_ROLES, 'workforce role');
      choice(rule.op, ['>=', '<='], 'workforce operator');
    }
  }
}

export function validateSearchFilters<T extends object>(
  raw: unknown,
  defaults: T,
  label: string,
  options: SearchFilterOptions = {}
): T {
  if (!record(raw)) fail(`${label} filters must be an object.`);
  const result = structuredClone(defaults) as Record<string, unknown>;
  for (const [key, value] of Object.entries(raw)) {
    if (!hasOwn(defaults, key)) fail(`unsupported ${label} criterion ${key}.`);
    if (['geoRadius', 'crmFilterSections', 'workforceRules'].includes(key)) {
      nestedFilter(key, value, options);
    } else {
      const initial = result[key];
      if (Array.isArray(initial)) {
        if (
          !Array.isArray(value) ||
          value.some((item) => typeof item !== 'string')
        )
          fail(`${key} must be a string array.`);
      } else if (typeof value !== typeof initial || value === null) {
        fail(`${key} has the wrong type.`);
      }
    }
    result[key] = structuredClone(value);
    if (key === 'geoRadius' && record(value))
      result[key] = { unit: 'km', ...value };
    if (
      typeof value === 'string' &&
      /(?:Min|Max)$/.test(key) &&
      value !== '' &&
      !Number.isFinite(Number(value))
    )
      fail(`${key} requires a finite number.`);
  }
  if (label === 'people') {
    const vocab: Record<string, readonly string[]> = {
      companySizes: COMPANY_SIZE_VALUES,
      seniority: SENIORITY_LEVELS,
      pastSeniority: SENIORITY_LEVELS,
      industriesInclude: [...INDUSTRIES, ...INDUSTRY_GROUP_NAMES],
      industriesExclude: [...INDUSTRIES, ...INDUSTRY_GROUP_NAMES],
      functionCategoriesInclude: LINKEDIN_FUNCTIONS,
      pastFunctionCategoriesInclude: LINKEDIN_FUNCTIONS,
      openToCards: OPEN_TO_CARD_OPTIONS.map((option) => option.value),
      // Sidebar-private pickers mirrored by API criteria_people vocab checks.
      degreeTypes: [
        'High School',
        'Associate',
        "Bachelor's",
        "Master's",
        'MBA',
        'PhD',
        'JD',
        'MD',
      ],
    };
    for (const [key, values] of Object.entries(vocab))
      for (const value of result[key] as string[]) choice(value, values, key);
    choice(
      result.jobTitleMatchType,
      ['contains', 'exact', 'similar'],
      'jobTitleMatchType'
    );
    choice(
      result.excludeInCleanlist,
      ['off', 'any', 'list'],
      'excludeInCleanlist'
    );
    choice(
      result.excludeInInstantly,
      ['off', 'any_campaign', 'statuses'],
      'excludeInInstantly'
    );
    for (const status of result.excludeInInstantlyStatuses as string[])
      choice(
        status,
        Object.keys(INSTANTLY_STATUS_LABELS),
        'excludeInInstantlyStatuses'
      );
    choice(
      result.yearsOfExperience,
      ['', '0-1', '1-2', '3-5', '6-10', '10+'],
      'yearsOfExperience'
    );
    choice(
      result.yearsAtCompany,
      ['', '0-1', '1-2', '2-5', '5-10', '10+'],
      'yearsAtCompany'
    );
    if (
      !Number.isInteger(result.maxResults) ||
      (result.maxResults as number) < 1 ||
      (result.maxResults as number) > 500
    )
      fail('maxResults must be an integer from 1 to 500.');
    const refused = options.allowExclusions
      ? PRIVATE_PEOPLE_FIELDS.filter((key) =>
          PERSON_LOOKUP_FIELDS.includes(key)
        )
      : PRIVATE_PEOPLE_FIELDS;
    for (const key of refused) {
      const value = result[key];
      if (
        Array.isArray(value)
          ? value.length > 0
          : typeof value === 'string' && value.length > 0
      )
        fail(`private criterion ${key} cannot be carried in a URL.`);
    }
    if ((result.limitPerCompany as string).trim() !== '')
      fail('limitPerCompany uses different portal and MCP page semantics.');
    const sections =
      result.crmFilterSections as SearchFilters['crmFilterSections'];
    result.crmFilterSections = {
      salesforce: (sections.salesforce ?? []).map((clause) => ({
        ...clause,
        polarity: clause.polarity ?? 'exclude',
      })),
      hubspot: (sections.hubspot ?? []).map((clause) => ({
        ...clause,
        polarity: clause.polarity ?? 'exclude',
      })),
    };
  } else {
    const vocab: Record<string, readonly string[]> = {
      headcountRanges: HEADCOUNT_RANGES.map((range) => range.value),
      headcountRangesExclude: HEADCOUNT_RANGES.map((range) => range.value),
      companyType: MCP_COMPANY_TYPES,
      companyTypeExclude: MCP_COMPANY_TYPES,
      fundingRoundTypes: FUNDING_ROUND_TYPES,
      fundingRoundTypesExclude: FUNDING_ROUND_TYPES,
      stockListings: STOCK_LISTING_OPTIONS.map((option) => option.value),
    };
    for (const [key, values] of Object.entries(vocab))
      for (const value of result[key] as string[]) choice(value, values, key);
    choice(
      result.excludeInCleanlist,
      ['off', 'any', 'list'],
      'excludeInCleanlist'
    );
    const sections =
      result.crmFilterSections as CompanySearchFilters['crmFilterSections'];
    result.crmFilterSections = {
      salesforce: (sections.salesforce ?? []).map((clause) => ({
        ...clause,
        polarity: clause.polarity ?? 'exclude',
      })),
      hubspot: (sections.hubspot ?? []).map((clause) => ({
        ...clause,
        polarity: clause.polarity ?? 'exclude',
      })),
    };
    if (carriesCompanyExcludes(result)) {
      if (!options.companyExcludes)
        fail('company exclusions cannot be carried here.');
      // What the page would refuse before any request: a list scope with no
      // lists, a clause that excludes people, a bad domain.
      validateCompanyExclusions(result as unknown as CompanySearchFilters);
    }
  }
  return result as T;
}

/** Whether a validated company filter blob names any exclude at all. */
export function carriesCompanyExcludes(
  company: Record<string, unknown>
): boolean {
  const sections = company.crmFilterSections as
    | Partial<CompanySearchFilters['crmFilterSections']>
    | undefined;
  return (
    ((company.excludeCompanyDomains as string[] | undefined) ?? []).length >
      0 ||
    ((company.excludeInCleanlist as string | undefined) ?? 'off') !== 'off' ||
    ((company.excludeInCleanlistListIds as string[] | undefined) ?? []).length >
      0 ||
    (sections?.salesforce ?? []).length > 0 ||
    (sections?.hubspot ?? []).length > 0
  );
}

/** Recognize an inner MCP-shaped payload even when its outside marker is lost.
 * Reserved source/version/context keys must never reach ordinary auto-hydration.
 */
export function decodeMcpSearchHandoff(input: {
  /** The dedicated mcpq transport. It must never enter ordinary q hydration. */
  query?: string;
  ordinaryQuery?: string;
  marker?: string;
  aiFilters?: string;
  tab?: string;
  href?: string;
}): McpHandoffDecode {
  const decoded: unknown = input.query ? decodeSearchQuery(input.query) : null;
  const legacy: unknown = input.ordinaryQuery
    ? decodeSearchQuery(input.ordinaryQuery)
    : null;
  const innerMarker =
    record(legacy) &&
    ['source', 'version', 'context'].some((key) => hasOwn(legacy, key));
  if (input.marker === undefined && input.query === undefined && !innerMarker)
    return { kind: 'ordinary' };
  try {
    if (input.marker !== '1')
      fail('the mcp=1 URL marker is missing or unsupported.');
    if (input.ordinaryQuery !== undefined)
      fail('MCP links cannot contain ordinary q. Request a new MCP link.');
    if (input.aiFilters !== undefined)
      fail('MCP links cannot also contain aiFilters.');
    if (
      (input.href?.length ?? 0) > MCP_HANDOFF_URL_LIMIT ||
      (input.query?.length ?? 0) > MCP_HANDOFF_URL_LIMIT
    )
      fail('the encoded URL exceeds 8192 characters.');
    if (!record(decoded))
      fail('mcpq is missing or is not valid UTF-8/base64 JSON.');
    // The ordinary codec remains permissive for compatibility. MCP input must
    // not replace invalid UTF-8 with replacement characters or accept junk.
    if (
      !input.query ||
      !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(
        input.query
      )
    )
      fail('mcpq is not valid base64.');
    new TextDecoder('utf-8', { fatal: true }).decode(
      Uint8Array.from(atob(input.query), (char) => char.charCodeAt(0))
    );
    exactKeys(
      decoded,
      ['source', 'version', 'tab', 'filters', 'context'],
      'envelope'
    );
    if (decoded.source !== 'mcp' || decoded.version !== 1)
      fail('the source or version is unsupported.');
    choice(decoded.tab, ['people', 'company'], 'tab');
    if (input.tab !== (decoded.tab === 'company' ? 'companies' : 'people'))
      fail('the URL tab does not match the payload.');
    if (!record(decoded.context)) fail('context must be an object.');
    exactKeys(
      decoded.context,
      ['limit', 'crm', 'sort', 'organizationId'],
      'context'
    );
    const context = decoded.context;
    if (
      typeof context.limit !== 'number' ||
      !Number.isInteger(context.limit) ||
      context.limit < 1 ||
      context.limit > 500
    )
      fail('limit must be an integer from 1 to 500.');
    if (context.crm === 'attio') fail(ATTIO_SEARCH_PAGE_REFUSAL);
    choice(context.crm, ['salesforce', 'hubspot', null], 'crm');
    if (
      context.sort !== null &&
      (typeof context.sort !== 'string' || !SORT_COLUMN.test(context.sort))
    )
      fail('sort is not a supported provider column.');
    if (
      typeof context.organizationId !== 'string' ||
      !context.organizationId.trim()
    )
      fail('organizationId is missing.');
    if (!record(decoded.filters)) fail('filters must be an object.');
    const { [MCP_COMPOSITE_KEY]: companySlice, ...peopleSlice } =
      decoded.filters;
    const people =
      decoded.tab === 'people'
        ? validateSearchFilters(peopleSlice, INITIAL_FILTERS, 'people')
        : structuredClone(INITIAL_FILTERS);
    const company =
      decoded.tab === 'company'
        ? validateSearchFilters(
            decoded.filters,
            INITIAL_COMPANY_FILTERS,
            'company'
          )
        : structuredClone(INITIAL_COMPANY_FILTERS);
    const composite =
      companySlice === undefined
        ? structuredClone(INITIAL_COMPANY_FILTERS)
        : validateSearchFilters(
            companySlice,
            INITIAL_COMPANY_FILTERS,
            'company'
          );
    if (
      decoded.tab === 'company' &&
      (context.crm !== null || context.sort !== null)
    )
      fail('company searches cannot carry CRM or sort context.');
    const completeContext: McpSearchContext = {
      limit: context.limit,
      crm: context.crm as McpSearchContext['crm'],
      sort: context.sort as string | null,
      organizationId: context.organizationId,
      maxResultsExplicit: hasOwn(peopleSlice, 'maxResults'),
      hasExplicitCompanyCriteria: companySlice !== undefined,
    };
    assertMcpPreviewLimit(
      completeContext,
      decoded.tab === 'people' ? people : undefined,
      decoded.tab === 'people' &&
        mcpCompositeRoute(people, composite, completeContext).useComposite
    );
    if (
      context.sort !== null &&
      mcpCompositeRoute(people, composite, completeContext).useComposite
    )
      fail('the MCP composite search did not apply the requested sort.');
    if (
      decoded.tab === 'people' &&
      context.crm === null &&
      (people.excludeCrmCustomers ||
        people.excludeCrmActiveOpportunities ||
        people.excludeCrmOwnedByMe ||
        people.excludeCrmOwnedByOthers ||
        people.excludeCrmAccountStatus)
    )
      fail('legacy CRM filters require their explicit provider.');
    // Company numeric/Boolean grammar validation is the existing real builder.
    buildCompanyFilters(company);
    buildCompanyFilters(composite);
    return {
      kind: 'valid',
      value: {
        tab: decoded.tab as 'people' | 'company',
        people,
        company,
        composite,
        context: completeContext,
      },
    };
  } catch (error) {
    return {
      kind: 'invalid',
      message:
        error instanceof Error
          ? error.message
          : 'Cannot restore MCP search: invalid payload.',
    };
  }
}

/** API explicit company_criteria semantics without changing the visible toggle. */
export function mcpCompositeRoute(
  people: SearchFilters,
  company: CompanySearchFilters,
  context: McpSearchContext | null
) {
  const route = detectCompositeRoute(people, company);
  if (route.useComposite && context)
    return {
      ...route,
      companyFilter: buildCompanyFilters(company),
    };
  if (!route.useComposite && context?.hasExplicitCompanyCriteria) {
    const compiled = buildCompanyFilters(company);
    if (Object.keys(compiled).length)
      return {
        useComposite: true,
        companyFilter: compiled,
        reason: 'explicit company criteria require a company-first search',
      };
  }
  return route;
}

/** Three proven compiler differences, pinned by the shared API fixture:
 * people.py excludes both display/full country names and uses substring
 * languages. Reuse the existing leaf/group builders and country catalogs.
 */
export function adaptMcpPeopleFilter<T extends Record<string, unknown>>(
  compiled: T,
  people: SearchFilters,
  context: McpSearchContext | null
): T {
  if (!context || !Array.isArray(compiled.filters)) return compiled;
  const result = structuredClone(compiled);
  const countries: Record<string, string[]> = {
    region_country_component: people.contactCountriesExclude,
    company_hq_location_country: people.companyCountriesExclude,
  };
  const countryNode = (node: unknown, column: string): boolean =>
    record(node) &&
    (Array.isArray(node.filters)
      ? node.filters.length > 0 &&
        node.filters.every((child) => countryNode(child, column))
      : node.column === column && node.type === 'not_contains');
  (result as Record<string, unknown>).filters = (
    compiled.filters as unknown[]
  ).map((node) => {
    if (!record(node)) return node;
    if (node.column === 'languages' && node.type === 'in') {
      const replacement: Record<string, unknown>[] = [];
      addFuzzyFilter(replacement, 'languages', people.languages);
      return replacement[0];
    }
    for (const [column, values] of Object.entries(countries)) {
      if (!values.length || !countryNode(node, column)) continue;
      const names = [
        ...new Set([
          ...expandCountrySelectionToDisplayNames(values),
          ...expandCountrySelectionToFullNames(values),
        ]),
      ];
      const replacement: Record<string, unknown>[] = [];
      addContainsFilter(replacement, column, names, 'not_contains');
      return replacement[0];
    }
    return node;
  });
  return result;
}

/** Rows one search call fetches: one 25-row page, which is one search on
 *  metered plans (Lev, 2026-10-30). The API clamps to the same size. */
export const SEARCH_PAGE_SIZE = 25;

/** Rows a search call asks for: one page, or fewer when an MCP handoff's
 *  explicit maxResults is smaller. */
export function mcpRequestLimit(
  context: McpSearchContext | null,
  people?: SearchFilters,
  composite = false
): number {
  if (!context) return SEARCH_PAGE_SIZE;
  return Math.min(SEARCH_PAGE_SIZE, mcpPreviewSize(context, people, composite));
}

/** The rows an MCP handoff previews in the portal, across pages. */
function mcpPreviewSize(
  context: McpSearchContext,
  people?: SearchFilters,
  composite = false
): number {
  const explicitMax =
    people &&
    (context.maxResultsExplicit ||
      people.maxResults !== INITIAL_FILTERS.maxResults);
  // The MCP limit is the chat preview's size, not a cap: floor it at the
  // 25-row page so the pager stays live. Only an explicit maxResults caps.
  const limit = Math.min(
    Math.max(context.limit, 25),
    explicitMax ? people.maxResults : 500
  );
  return people && !composite ? Math.min(100, limit) : limit;
}

/** Existing preview caps are authoritative. No ordinary/server cap is changed. */
export function assertMcpPreviewLimit(
  context: McpSearchContext | null,
  people?: SearchFilters,
  composite = false
) {
  if (
    context &&
    (!people || composite) &&
    mcpPreviewSize(context, people, composite) > 100
  ) {
    fail(
      'the portal preview caps this search at 100 rows, below the executed MCP limit.'
    );
  }
}
